import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

import { useProfileStore } from '@entities/viewer'
import { getFirstQueryValue } from '@shared/lib/routes'

import { useButtonFiltersSubmit } from './button-filters-submit.hook'
import { useDemoShiftControlStore } from './store'

interface Option {
  text: string
  from: string
  to: string
  selected: boolean
}

interface SwitcherSlotsTrackerReturn {
  options: ComputedRef<Option[]>
  disabled: ComputedRef<boolean>
  setChangeSlot: (from: Option['from'], to: Option['to']) => Promise<void>
}

export const useSwitcherSlotsTracker = (): SwitcherSlotsTrackerReturn => {
  const route = useRoute()
  const profileStore = useProfileStore()
  const demoShiftControlStore = useDemoShiftControlStore()
  const { setSubmit } = useButtonFiltersSubmit()

  const initFromQuery = (): void => {
    const fromQuery = getFirstQueryValue(route?.query?.from)
    const toQuery = getFirstQueryValue(route?.query?.to)

    if (fromQuery) demoShiftControlStore.setFilterFrom(fromQuery)
    if (toQuery) demoShiftControlStore.setFilterTo(toQuery)
  }

  initFromQuery()

  const from = computed(() => profileStore.getCurrentTimeSlotFrom!)
  const to = computed(() => profileStore.getCurrentTimeSlotTo!)

  const prevFrom = computed(() => profileStore.getPrevTimeSlotFrom!)
  const prevTo = computed(() => profileStore.getPrevTimeSlotTo!)

  const nextFrom = computed(() => profileStore.getNextTimeSlotFrom!)
  const nextTo = computed(() => profileStore.getNextTimeSlotTo!)

  const disabled = computed(() => demoShiftControlStore.isShiftControlBusy)

  const options = computed(() => {
    return [
      {
        text: dayjs(prevFrom.value).format(DateFormat.DefaultTime),
        from: prevFrom.value,
        to: prevTo.value,
        selected: demoShiftControlStore.getFilterFrom === prevFrom.value,
      },
      {
        text: `Current: ${dayjs(from.value).format(DateFormat.DefaultTime)}`,
        from: from.value,
        to: to.value,
        selected: demoShiftControlStore.getFilterFrom === from.value,
      },
      {
        text: dayjs(nextFrom.value).format(DateFormat.DefaultTime),
        from: nextFrom.value,
        to: nextTo.value,
        selected: demoShiftControlStore.getFilterFrom === nextFrom.value,
      },
    ]
  })

  const setChangeSlot = async (from: Option['from'], to: Option['to']): Promise<void> => {
    demoShiftControlStore.setFilterReservationType(null)
    demoShiftControlStore.setFilterSlotStatus(null)
    demoShiftControlStore.setFilterFrom(from)
    demoShiftControlStore.setFilterTo(to)
    await setSubmit()
  }

  return {
    options,
    disabled,
    setChangeSlot,
  }
}
